import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { authService } from '../../../services/api';
import { ChevronDown, RotateCw } from 'lucide-react';
import DateRangePicker from '../../../components/DateRangePicker';
import { fetchStates, fetchCitiesForState, INDIA_LOCATIONS } from '../../../services/locationService';

const renderSignupAsBadge = (signupAs) => {
  const type = signupAs || 'Vendor/Supplier';
  let badgeStyle = 'bg-teal-50 text-teal-700 border-teal-200';
  if (type === 'Architect/Designer') {
    badgeStyle = 'bg-indigo-50 text-indigo-700 border-indigo-200';
  } else if (type === 'User') {
    badgeStyle = 'bg-amber-50 text-amber-700 border-amber-200';
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeStyle} whitespace-nowrap`}>
      {type}
    </span>
  );
};

const formatDateTimeParts = (dateStr) => {
  if (!dateStr) return { date: 'N/A', time: '' };
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { date: 'N/A', time: '' };
    const pad = (num) => String(num).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return { date: `${day}/${month}/${year}`, time: `${hours}:${minutes}` };
  } catch (err) {
    return { date: 'N/A', time: '' };
  }
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const EditCitiesModal = ({ user, onClose, onSaveSuccess, toast }) => {
  const [selectedCities, setSelectedCities] = useState(new Set(user.assigned_cities || []));
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStates, setExpandedStates] = useState({});
  const [saving, setSaving] = useState(false);

  const allIndiaCitiesList = React.useMemo(() => {
    const locations = INDIA_LOCATIONS || {};
    const list = [];
    Object.values(locations).forEach(arr => arr.forEach(c => list.push(c)));
    return list;
  }, []);

  const isAllCitiesSelected = selectedCities.has('ALL') || (allIndiaCitiesList.length > 0 && allIndiaCitiesList.every(c => selectedCities.has(c)));

  const selectAllCities = () => {
    setSelectedCities(new Set(['ALL', ...allIndiaCitiesList]));
  };

  const clearAll = () => {
    setSelectedCities(new Set());
  };

  const toggleCity = (city) => {
    setSelectedCities(prev => {
      const next = new Set(prev);
      if (next.has('ALL')) next.delete('ALL');
      if (next.has(city)) {
        next.delete(city);
      } else {
        next.add(city);
      }
      return next;
    });
  };

  const removeCity = (city) => {
    setSelectedCities(prev => {
      const next = new Set(prev);
      if (next.has('ALL')) next.delete('ALL');
      next.delete(city);
      return next;
    });
  };

  const toggleStateExpand = (stateName) => {
    setExpandedStates(prev => ({ ...prev, [stateName]: !prev[stateName] }));
  };

  const toggleStateAll = (stateName, citiesInState) => {
    setSelectedCities(prev => {
      const next = new Set(prev);
      if (next.has('ALL')) next.delete('ALL');
      const allSelected = citiesInState.every(c => next.has(c));
      if (allSelected) {
        citiesInState.forEach(c => next.delete(c));
      } else {
        citiesInState.forEach(c => next.add(c));
      }
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const citiesArray = isAllCitiesSelected ? ['ALL'] : Array.from(selectedCities).filter(c => c !== 'ALL');
      const res = await authService.updateUserCities(user.email, citiesArray);
      if (res.success) {
        toast.success(`City access updated for ${user.name}`);
        onSaveSuccess();
        onClose();
      } else {
        toast.error("Failed to update city access: " + (res.error || res.message));
      }
    } catch (err) {
      toast.error("An error occurred while updating city access.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Filter INDIA_LOCATIONS by searchTerm
  const locationsObj = INDIA_LOCATIONS || {};
  const filteredStates = Object.keys(locationsObj).reduce((acc, stateName) => {
    const cities = locationsObj[stateName] || [];
    if (!searchTerm.trim()) {
      acc[stateName] = cities;
    } else {
      const term = searchTerm.toLowerCase().trim();
      const stateMatch = stateName.toLowerCase().includes(term);
      const matchingCities = cities.filter(c => c.toLowerCase().includes(term));
      if (stateMatch || matchingCities.length > 0) {
        acc[stateName] = stateMatch ? cities : matchingCities;
      }
    }
    return acc;
  }, {});

  const isSearching = !!searchTerm.trim();

  // Group assigned cities by state
  const assignedCitiesByState = React.useMemo(() => {
    const map = {};
    const locations = INDIA_LOCATIONS || {};
    const matchedCities = new Set();

    Object.keys(locations).sort().forEach(stateName => {
      const allCitiesInState = locations[stateName] || [];
      const selectedInState = allCitiesInState.filter(c => selectedCities.has(c));
      if (selectedInState.length > 0) {
        const isAllSelected = selectedInState.length === allCitiesInState.length;
        map[stateName] = {
          cities: selectedInState,
          isAll: isAllSelected,
          allCities: allCitiesInState
        };
        selectedInState.forEach(c => matchedCities.add(c));
      }
    });

    const leftover = Array.from(selectedCities).filter(c => c !== 'ALL' && !matchedCities.has(c));
    if (leftover.length > 0) {
      map['Other'] = {
        cities: leftover,
        isAll: false,
        allCities: leftover
      };
    }

    return map;
  }, [selectedCities]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 md:p-4 overflow-hidden">
      <div className="bg-white max-w-2xl w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[90vh] my-auto">
        <div className="p-3.5 px-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
          <div>
            <span className="text-[10px] uppercase font-bold text-teal-600 tracking-wider">City Access Privileges</span>
            <h3 className="text-sm font-bold text-gray-800 mt-0.5">{user.name} <span className="text-xs font-normal text-gray-400">({user.email})</span></h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded-lg hover:bg-gray-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave} className="flex flex-col flex-1 overflow-hidden min-h-0">
          <div className="p-4 space-y-3 overflow-y-auto flex-1 text-left min-h-0">
            {/* Selected Chips Area grouped by state */}
            <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200/70">
              <div className="flex items-center justify-between mb-1.5 select-none">
                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  Assigned Cities ({isAllCitiesSelected ? 'All Cities' : selectedCities.size})
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={isAllCitiesSelected ? clearAll : selectAllCities}
                    className={`text-[11px] font-bold px-2 py-0.5 rounded border transition-all cursor-pointer ${
                      isAllCitiesSelected
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
                        : 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100'
                    }`}
                  >
                    {isAllCitiesSelected ? '✓ All Cities Selected' : '🌐 Select All Cities'}
                  </button>
                  {selectedCities.size > 0 && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="text-[11px] font-bold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
              {isAllCitiesSelected ? (
                <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                    🌐 All Cities Selected (Unrestricted Access Across India)
                  </span>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-[11px] font-bold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
                  >
                    Deselect All
                  </button>
                </div>
              ) : selectedCities.size === 0 ? (
                <p className="text-xs text-amber-700 font-medium bg-amber-50/80 p-2 rounded-lg border border-amber-200/80">
                  ⚠️ No cities currently assigned. This admin will not be able to view any city-scoped data.
                </p>
              ) : (
                <div className="max-h-28 overflow-y-auto pr-1 space-y-1 text-xs">
                  {Object.keys(assignedCitiesByState).sort().map(stateName => {
                    const { cities, isAll, allCities } = assignedCitiesByState[stateName];
                    return (
                      <div key={stateName} className="flex flex-wrap items-center gap-1 bg-white p-1 px-2 rounded-lg border border-slate-200/60 shadow-2xs">
                        <span className="font-bold text-slate-800 text-[11px] shrink-0">{stateName} :</span>
                        {isAll ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-800 border border-teal-300">
                            ALL
                            <button
                              type="button"
                              onClick={() => toggleStateAll(stateName, allCities)}
                              className="text-teal-600 hover:text-rose-600 font-extrabold ml-0.5 cursor-pointer"
                              title={`Remove all cities in ${stateName}`}
                            >
                              ×
                            </button>
                          </span>
                        ) : (
                          cities.map(city => (
                            <span key={city} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-800 border border-teal-200">
                              {city}
                              <button
                                type="button"
                                onClick={() => removeCity(city)}
                                className="text-teal-400 hover:text-rose-500 font-bold ml-0.5 cursor-pointer"
                                title={`Remove ${city}`}
                              >
                                ×
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search state or city name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-1.5 text-xs focus:border-teal-500 focus:ring-1 focus:ring-teal-500 font-medium text-gray-800 outline-none"
              />
              <svg className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2 text-xs text-gray-400 hover:text-gray-600 font-bold"
                >
                  Clear
                </button>
              )}
            </div>

            {/* States & Cities Accordion */}
            <div className="space-y-1.5 border border-gray-200 rounded-xl p-2.5 bg-white max-h-60 overflow-y-auto">
              {Object.keys(filteredStates).length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6 font-medium">No cities or states found matching "{searchTerm}"</p>
              ) : (
                Object.keys(filteredStates).sort().map(stateName => {
                  const cities = filteredStates[stateName];
                  const selectedCountInState = isAllCitiesSelected ? cities.length : cities.filter(c => selectedCities.has(c)).length;
                  const allSelectedInState = isAllCitiesSelected || (cities.length > 0 && selectedCountInState === cities.length);
                  const isExpanded = isSearching || !!expandedStates[stateName];

                  return (
                    <div key={stateName} className="border border-gray-100 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50/70 hover:bg-gray-100/70 select-none transition-colors">
                        <button
                          type="button"
                          onClick={() => toggleStateExpand(stateName)}
                          className="flex items-center gap-1.5 text-xs font-bold text-gray-700 hover:text-gray-900 cursor-pointer flex-1 text-left"
                        >
                          <span className="text-gray-400 text-[10px]">{isExpanded ? '▼' : '▶'}</span>
                          <span>{stateName}</span>
                          <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${selectedCountInState > 0 ? 'bg-teal-100 text-teal-700' : 'bg-gray-200 text-gray-500'}`}>
                            {selectedCountInState}/{cities.length} selected
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => toggleStateAll(stateName, cities)}
                          className="text-[11px] font-bold text-teal-600 hover:text-teal-800 cursor-pointer px-2 py-0.5 rounded hover:bg-teal-50"
                        >
                          {allSelectedInState ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="p-2 bg-white grid grid-cols-2 sm:grid-cols-3 gap-1.5 border-t border-gray-100">
                          {cities.map(city => {
                            const isChecked = isAllCitiesSelected || selectedCities.has(city);
                            return (
                              <label
                                key={city}
                                className={`flex items-center gap-1.5 p-1 px-2 rounded-lg border text-xs font-semibold cursor-pointer transition-all ${
                                  isChecked
                                    ? 'bg-teal-50/70 border-teal-200 text-teal-800'
                                    : 'border-gray-100 hover:bg-gray-50 text-gray-700'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleCity(city)}
                                  className="rounded text-teal-600 focus:ring-teal-500 w-3.5 h-3.5 cursor-pointer"
                                />
                                <span className="truncate">{city}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex justify-between items-center gap-3 border-t border-gray-100 py-2.5 px-4 bg-gray-50/50 flex-shrink-0 select-none">
            <span className="text-xs text-gray-500 font-semibold">
              {isAllCitiesSelected ? 'All cities selected' : `${selectedCities.size} cities selected`}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-100 active:scale-[0.98] transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold active:scale-[0.98] transition-all shadow-md shadow-teal-600/10 cursor-pointer disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const UnassignedCityAuditModal = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [auditData, setAuditData] = useState(null);

  useEffect(() => {
    const fetchAudit = async () => {
      setLoading(true);
      try {
        const res = await authService.getUnassignedCityAudit();
        if (res && res.success) {
          const auditObj = res.data && typeof res.data === 'object' ? res.data : res;
          setAuditData(auditObj);
        } else {
          setAuditData(null);
        }
      } catch (err) {
        console.error("Error loading unassigned city audit:", err);
        setAuditData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
  }, []);

  const roleAuditList = auditData?.roleAudit || [];
  const totalVendorCities = auditData?.totalVendorCities ?? 0;
  const totalGaps = auditData?.totalGaps ?? 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-hidden select-none">
      <div className="bg-white max-w-2xl w-full rounded-2xl shadow-2xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh] my-auto">
        <div className="p-4 px-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
          <div>
            <span className="text-[10px] uppercase font-bold text-teal-600 tracking-wider">Role Coverage Audit</span>
            <h3 className="text-sm font-bold text-gray-800 mt-0.5">
              Unassigned Cities with Listings / Vendors
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded-lg hover:bg-gray-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1 text-left min-h-0">
          {loading ? (
            <div className="py-12 text-center text-xs font-semibold text-gray-400 animate-pulse">
              Evaluating role city coverage across all database listings...
            </div>
          ) : !auditData ? (
            <div className="py-12 text-center text-xs font-semibold text-rose-500">
              Failed to evaluate unassigned city audit.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-200/70 text-xs">
                <div>
                  <span className="font-bold text-gray-800">Total Vendor & Listing Cities:</span>{' '}
                  <span className="font-semibold text-teal-700">{totalVendorCities} Cities</span>
                </div>
                <div>
                  <span className="font-bold text-gray-800">Total Unallocated Gaps:</span>{' '}
                  <span className={`font-semibold ${totalGaps > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {totalGaps} Gaps
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {roleAuditList.map(item => {
                  const unassignedCitiesList = item?.unassignedCities || [];
                  const unassignedCount = item?.unassignedCount ?? unassignedCitiesList.length;
                  return (
                    <div key={item.role} className="p-3.5 bg-white rounded-xl border border-gray-200 shadow-2xs space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-800 text-xs">{item.roleLabel}</span>
                          <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                            {item.totalAssignedAdmins || 0} {item.totalAssignedAdmins === 1 ? 'Admin' : 'Admins'} Assigned
                          </span>
                        </div>
                        {unassignedCount === 0 ? (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                            ✓ 100% City Coverage
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">
                            ⚠️ {unassignedCount} Unassigned {unassignedCount === 1 ? 'City' : 'Cities'}
                          </span>
                        )}
                      </div>

                      {unassignedCount === 0 ? (
                        <p className="text-[11px] text-gray-400 font-medium italic">
                          All listing/vendor cities are covered by assigned admins for this role.
                        </p>
                      ) : (
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5">
                            Listings / Vendors Exist But Not Assigned to Any {item.roleLabel}:
                          </span>
                          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                            {unassignedCitiesList.map(c => (
                              <span key={c} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                {c}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="p-3 px-5 border-t border-gray-100 bg-gray-50/50 flex justify-end flex-shrink-0">
          <button onClick={onClose} className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition-all cursor-pointer">
            Close Audit
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewCitiesModal = ({ user, onClose }) => {
  const isAllCities = React.useMemo(() => {
    const assigned = user.assigned_cities || [];
    return assigned.some(c => c.toUpperCase() === 'ALL' || c === 'All Cities');
  }, [user]);

  const assignedCitiesByState = React.useMemo(() => {
    const map = {};
    const locations = INDIA_LOCATIONS || {};
    const matchedCities = new Set();
    const assignedSet = new Set(user.assigned_cities || []);

    Object.keys(locations).sort().forEach(stateName => {
      const allCitiesInState = locations[stateName] || [];
      const selectedInState = allCitiesInState.filter(c => assignedSet.has(c));
      if (selectedInState.length > 0) {
        const isAllSelected = selectedInState.length === allCitiesInState.length;
        map[stateName] = {
          cities: selectedInState,
          isAll: isAllSelected
        };
        selectedInState.forEach(c => matchedCities.add(c));
      }
    });

    const leftover = Array.from(assignedSet).filter(c => c !== 'ALL' && !matchedCities.has(c));
    if (leftover.length > 0) {
      map['Other'] = {
        cities: leftover,
        isAll: false
      };
    }

    return map;
  }, [user]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-hidden select-none">
      <div className="bg-white max-w-md w-full rounded-2xl shadow-2xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[80vh] my-auto">
        <div className="p-3.5 px-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
          <div>
            <span className="text-[10px] uppercase font-bold text-teal-600 tracking-wider">Assigned Cities Overview</span>
            <h3 className="text-sm font-bold text-gray-800 mt-0.5">{user.name} <span className="text-xs font-normal text-gray-400">({isAllCities ? 'All Cities' : `${user.assigned_cities?.length || 0} cities`})</span></h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded-lg hover:bg-gray-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-2 overflow-y-auto flex-1 text-left text-xs min-h-0">
          {isAllCities ? (
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-center space-y-1 my-2">
              <span className="text-sm font-bold text-emerald-800 flex items-center justify-center gap-1.5">
                🌐 All Cities (Full Access)
              </span>
              <p className="text-xs text-emerald-600 font-medium">
                This admin has unrestricted access to all cities across India.
              </p>
            </div>
          ) : Object.keys(assignedCitiesByState).length === 0 ? (
            <p className="text-gray-400 text-center py-6">No cities assigned.</p>
          ) : (
            Object.keys(assignedCitiesByState).sort().map(stateName => {
              const { cities, isAll } = assignedCitiesByState[stateName];
              return (
                <div key={stateName} className="p-2.5 bg-gray-50/70 rounded-xl border border-gray-100 flex flex-col gap-1">
                  <span className="font-semibold text-gray-800 text-xs">{stateName} :</span>
                  <span className="text-gray-600 font-normal leading-relaxed">
                    {isAll ? <span className="font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">All</span> : cities.join(', ')}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="p-3 px-4 border-t border-gray-100 bg-gray-50/50 flex justify-end flex-shrink-0">
          <button onClick={onClose} className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition-all cursor-pointer">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageUsersTab = ({ toast }) => {
  const { hasPermission } = useAuth();

  // Categories and pagination states
  const [userCategory, setUserCategory] = useState('admins'); // 'admins', 'users_vendors'
  const [userFilters, setUserFilters] = useState({ startDate: '', endDate: '' });
  const [userPage, setUserPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_manage_users');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setUserPage = useCallback((valOrFn) => {
    setUserPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_manage_users', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [userPageSize, setUserPageSize] = useState(25);

  // Data states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Location states for Admin creation
  const [statesList, setStatesList] = useState([]);
  const [adminCitiesList, setAdminCitiesList] = useState([]);
  const [loadingAdminCities, setLoadingAdminCities] = useState(false);

  useEffect(() => {
    const loadStates = async () => {
      const list = await fetchStates();
      setStatesList(list);
    };
    loadStates();
  }, []);

  const handleAdminStateChange = async (stateVal) => {
    setAdminForm(prev => ({ ...prev, state: stateVal, city: '' }));
    if (stateVal) {
      setLoadingAdminCities(true);
      const cities = await fetchCitiesForState(stateVal);
      setAdminCitiesList(cities);
      setLoadingAdminCities(false);
    } else {
      setAdminCitiesList([]);
    }
  };

  // Create admin modal states
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [adminForm, setAdminForm] = useState({
    name: '',
    emailId: '',
    mobileNo: '',
    password: '',
    state: '',
    city: '',
    roles: []
  });

  // Edit user roles states
  const [editUserRoles, setEditUserRoles] = useState(null);

  // Edit user city access states
  const [editUserCities, setEditUserCities] = useState(null);

  // View cities modal state
  const [viewCitiesModalUser, setViewCitiesModalUser] = useState(null);

  // Unassigned city audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);

  // Action menu dropdown state
  const [activeActionMenuId, setActiveActionMenuId] = useState(null);

  useEffect(() => {
    const handleOutsideClick = () => setActiveActionMenuId(null);
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // Password change states
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete user states
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null);
  const [deleteInputConfirm, setDeleteInputConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!hasPermission('MANAGE_USERS')) return;
    setLoading(true);
    setError('');
    try {
      const res = await authService.getAdminUsers();
      if (res.success) {
        setUsers(res.data || []);
      } else {
        setError(res.error || 'Failed to fetch users registry.');
      }
    } catch (err) {
      setError('An error occurred while fetching users.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateAdminSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (adminForm.roles.length === 0) {
      toast.warning('Please select at least one role.');
      return;
    }

    try {
      const res = await authService.adminCreateUser(adminForm);
      if (res.success) {
        toast.success(`Admin user ${adminForm.emailId} created successfully.`);
        setAdminForm({ name: '', emailId: '', mobileNo: '', password: '', state: '', city: '', roles: [] });
        setShowCreateAdminModal(false);
        fetchUsers();
      } else {
        toast.error("Failed to create admin: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while creating admin user.");
      console.error(err);
    }
  };

  const handleRoleCheckboxChange = (role) => {
    const isChecked = adminForm.roles.includes(role);
    if (isChecked) {
      setAdminForm({ ...adminForm, roles: adminForm.roles.filter(r => r !== role) });
    } else {
      setAdminForm({ ...adminForm, roles: [...adminForm.roles, role] });
    }
  };

  const handleEditUserRolesSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const res = await authService.updateUserRoles(editUserRoles.email, editUserRoles.roles);
      if (res.success) {
        toast.success(`User roles updated successfully.`);
        setEditUserRoles(null);
        fetchUsers();
      } else {
        toast.error("Failed to update user roles: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while updating user roles.");
      console.error(err);
    }
  };

  const handleEditUserRoleCheckboxChange = (role) => {
    const isChecked = editUserRoles.roles.includes(role);
    if (isChecked) {
      setEditUserRoles({ ...editUserRoles, roles: editUserRoles.roles.filter(r => r !== role) });
    } else {
      setEditUserRoles({ ...editUserRoles, roles: [...editUserRoles.roles, role] });
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      toast.warning('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await authService.updateUserPassword(changePasswordUser.email, newPassword);
      if (res.success) {
        toast.success(`Password updated and notification email sent to ${changePasswordUser.email} successfully.`);
        setChangePasswordUser(null);
        setNewPassword('');
        setConfirmNewPassword('');
        fetchUsers();
      } else {
        toast.error("Failed to update password: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while updating user password.");
      console.error(err);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteUserSubmit = async (e) => {
    e.preventDefault();
    if (!deleteUserConfirm) return;
    if (deleteInputConfirm !== 'DELETE') {
      toast.error("Please type 'DELETE' to confirm deletion");
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await authService.adminDeleteUser(deleteUserConfirm.id);
      if (res.success) {
        toast.success(res.message || "User and associated data deleted successfully");
        setDeleteUserConfirm(null);
        setDeleteInputConfirm('');
        fetchUsers();
      } else {
        toast.error(res.message || "Failed to delete user");
      }
    } catch (err) {
      toast.error(err.message || "Error deleting user");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading users directory...</span>
      </div>
    );
  }

  // Dynamic filter state and city options from users data & statesList
  const allStatesOptions = Array.from(
    new Set([
      ...statesList,
      ...users.map(u => u.state).filter(Boolean)
    ])
  ).sort();

  const allCitiesOptions = Array.from(
    new Set(
      users
        .filter(u => !(userFilters.state) || (u.state && u.state.toLowerCase() === userFilters.state.toLowerCase()))
        .map(u => u.city)
        .filter(Boolean)
    )
  ).sort();

  // Filter users by date range, state, and city
  const filteredUsers = users.filter(u => {
    if (u.createdAt) {
      const itemDateStr = u.createdAt.substring(0, 10);
      if (userFilters.startDate && itemDateStr < userFilters.startDate) return false;
      if (userFilters.endDate && itemDateStr > userFilters.endDate) return false;
    } else if (userFilters.startDate || userFilters.endDate) {
      return false;
    }

    if (userFilters.state) {
      if (!u.state || u.state.toLowerCase() !== userFilters.state.toLowerCase()) return false;
    }

    if (userFilters.city) {
      if (!u.city || u.city.toLowerCase() !== userFilters.city.toLowerCase()) return false;
    }

    return true;
  });

  // Filter & sort admin vs vendor/client accounts
  const adminUsers = filteredUsers.filter(u =>
    u.roles && u.roles.some(role =>
      ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SALES_ADMIN', 'ROLE_LISTING_ADMIN', 'ROLE_SUPPORT_ADMIN', 'ROLE_MAGAZINE_SALES', 'ROLE_SEO_ADMIN'].includes(role)
    )
  ).sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt) : 0;
    const db = b.createdAt ? new Date(b.createdAt) : 0;
    return db - da;
  });

  const vendorOrClientUsers = filteredUsers.filter(u =>
    !u.roles || !u.roles.some(role =>
      ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SALES_ADMIN', 'ROLE_LISTING_ADMIN', 'ROLE_SUPPORT_ADMIN', 'ROLE_MAGAZINE_SALES', 'ROLE_SEO_ADMIN'].includes(role)
    )
  ).sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt) : 0;
    const db = b.createdAt ? new Date(b.createdAt) : 0;
    return db - da;
  });

  const activeUserList = userCategory === 'admins' ? adminUsers : vendorOrClientUsers;
  const userTotalPages = Math.ceil(activeUserList.length / userPageSize);
  const safeUserPage = Math.min(userPage, Math.max(0, userTotalPages - 1));
  const displayedUserList = activeUserList.slice(safeUserPage * userPageSize, (safeUserPage + 1) * userPageSize);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left">
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl mb-6 flex items-center gap-3 shadow-sm select-none">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      <div className="flex justify-between items-center mb-6 gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">User Audit & Roles</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAuditModal(true)}
            className="bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 text-xs sm:text-sm font-semibold py-1.5 px-3 sm:py-2 sm:px-4 rounded-lg shadow-2xs active:scale-[0.98] transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Unassigned City Audit
          </button>
          <button
            type="button"
            onClick={() => setShowCreateAdminModal(true)}
            className="bg-[#2ab391] hover:bg-[#23977a] text-white text-xs sm:text-sm font-semibold py-1.5 px-3 sm:py-2 sm:px-4 rounded-lg shadow-md shadow-[#2ab391]/10 active:scale-[0.98] transition-all flex-shrink-0 cursor-pointer"
          >
            Add System Admin
          </button>
        </div>
      </div>

      {/* User Category Sub-tabs */}
      <div className="border-b border-gray-200 mb-4 select-none">
        <div className="flex gap-2 sm:gap-6">
          <button
            onClick={() => {
              setUserCategory('admins');
              setUserPage(0);
            }}
            className={`pb-3 px-4 text-xs sm:text-sm font-bold border-b-2 transition-all cursor-pointer ${userCategory === 'admins'
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            Administrative Accounts ({adminUsers.length})
          </button>
          <button
            onClick={() => {
              setUserCategory('users_vendors');
              setUserPage(0);
            }}
            className={`pb-3 px-4 text-xs sm:text-sm font-bold border-b-2 transition-all cursor-pointer ${userCategory === 'users_vendors'
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            User & Vendor Accounts ({vendorOrClientUsers.length})
          </button>
        </div>
      </div>

      {/* Filter Controls Row (Below Sub-tabs - Matching Image 2) */}
      <div className="bg-gray-50/70 p-3 rounded-2xl border border-gray-100/80 flex flex-wrap items-center gap-3 sm:gap-4 mb-6 select-none">
        {/* Date Range Picker */}
        <div className="flex items-center gap-2 bg-white border border-gray-200/80 p-1.5 px-3 rounded-xl shadow-2xs">
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">DATE:</span>
          <DateRangePicker
            startDate={userFilters.startDate}
            endDate={userFilters.endDate}
            onChange={(start, end) => {
              setUserFilters(prev => ({ ...prev, startDate: start, endDate: end }));
              setUserPage(0);
            }}
            placeholder="Select Date Range"
            className="!w-48 text-xs"
          />
        </div>

        {/* State Filter */}
        <div className="flex items-center gap-2 bg-white border border-gray-200/80 p-1.5 px-3 rounded-xl shadow-2xs">
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">STATE:</span>
          <select
            value={userFilters.state || ''}
            onChange={(e) => {
              const stateVal = e.target.value;
              setUserFilters(prev => ({ ...prev, state: stateVal, city: '' }));
              setUserPage(0);
            }}
            className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-1 px-2.5 text-xs font-semibold text-gray-700 focus:outline-none focus:border-[#2ab391] cursor-pointer max-w-[150px] truncate shadow-2xs transition-colors"
          >
            <option value="">All States</option>
            {allStatesOptions.map(st => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>

        {/* City Filter */}
        <div className="flex items-center gap-2 bg-white border border-gray-200/80 p-1.5 px-3 rounded-xl shadow-2xs">
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">CITY:</span>
          <select
            value={userFilters.city || ''}
            onChange={(e) => {
              setUserFilters(prev => ({ ...prev, city: e.target.value }));
              setUserPage(0);
            }}
            className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-1 px-2.5 text-xs font-semibold text-gray-700 focus:outline-none focus:border-[#2ab391] cursor-pointer max-w-[150px] truncate shadow-2xs transition-colors"
          >
            <option value="">All Cities</option>
            {allCitiesOptions.map(ct => (
              <option key={ct} value={ct}>{ct}</option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        {(userFilters.startDate || userFilters.endDate || userFilters.state || userFilters.city) && (
          <button
            onClick={() => {
              setUserFilters({ startDate: '', endDate: '', state: '', city: '' });
              setUserPage(0);
            }}
            className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 border border-red-200/50 rounded-xl transition-colors cursor-pointer"
          >
            Clear
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[850px] lg:min-w-[950px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
              <th className="p-4 pl-6 whitespace-nowrap">User Name</th>
              <th className="p-4 whitespace-nowrap">Contact Details</th>
              <th className="p-4 whitespace-nowrap">Location</th>
              {userCategory === 'users_vendors' && (
                <th className="p-4 whitespace-nowrap">Sign-up Type</th>
              )}
              <th className="p-4 whitespace-nowrap">Roles</th>
              {userCategory === 'admins' && (
                <th className="p-4 whitespace-nowrap">Assigned Cities</th>
              )}
              <th className="p-4 whitespace-nowrap">Created At</th>
              <th className="p-4 pr-6 text-right whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
            {activeUserList.length === 0 ? (
              <tr>
                <td colSpan={userCategory === 'users_vendors' ? 7 : (userCategory === 'admins' ? 7 : 6)} className="p-12 text-center text-gray-400 font-medium">
                  No accounts found in this category.
                </td>
              </tr>
            ) : (
              displayedUserList.map(u => (
                <tr key={u.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-100 last:border-0">
                  <td className="p-4 pl-6 text-left font-medium text-gray-800 whitespace-nowrap">{u.name}</td>
                  <td className="p-4 text-left whitespace-nowrap">
                    <div className="font-medium text-gray-700 text-xs">{u.emailId}</div>
                    <div className="text-[11px] font-normal text-gray-500 mt-0.5">{u.mobileNo || 'N/A'}</div>
                  </td>
                  <td className="p-4 text-left whitespace-nowrap text-xs font-normal text-gray-600">
                    {u.city && u.state ? `${u.city}, ${u.state}` : u.state || u.city || 'N/A'}
                  </td>
                  {userCategory === 'users_vendors' && (
                    <td className="p-4 text-left whitespace-nowrap">{renderSignupAsBadge(u.signupAs)}</td>
                  )}
                  <td className="p-4 text-left min-w-[130px]">
                    <div className="flex flex-wrap gap-1.5">
                      {u.roles.map(r => (
                        <span key={r} className={`px-2 py-0.5 text-[9px] font-medium rounded uppercase ${r === 'ROLE_SUPER_ADMIN' ? 'bg-violet-50 text-violet-700 border border-violet-100' :
                          r === 'ROLE_SALES_ADMIN' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                            r === 'ROLE_LISTING_ADMIN' ? 'bg-orange-50 text-orange-700 border border-orange-100' :
                              r === 'ROLE_SUPPORT_ADMIN' ? 'bg-rose-50 text-rose-700 border border-rose-100' :
                                r === 'ROLE_MAGAZINE_SALES' ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' :
                                  r === 'ROLE_SEO_ADMIN' ? 'bg-teal-50 text-teal-700 border border-teal-100' :
                                    'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}>
                          {r.replace('ROLE_', '').replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  {userCategory === 'admins' && (
                    <td className="p-4 text-left whitespace-nowrap">
                      {u.roles.some(r => r === 'ROLE_SUPER_ADMIN' || r === 'ROLE_ADMIN') || (u.assigned_cities && u.assigned_cities.some(c => c.toUpperCase() === 'ALL' || c === 'All Cities')) ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          🌐 All Cities
                        </span>
                      ) : u.assigned_cities && u.assigned_cities.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setViewCitiesModalUser(u)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 cursor-pointer transition-all shadow-2xs"
                        >
                          View Cities ({u.assigned_cities.length})
                        </button>
                      ) : u.roles.includes('ROLE_SEO_ADMIN') && !u.roles.some(r => ['ROLE_LISTING_ADMIN', 'ROLE_SALES_ADMIN', 'ROLE_SUPPORT_ADMIN'].includes(r)) ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
                          🌐 Platform Wide (All India)
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          ⚠️ No Cities Assigned
                        </span>
                      )}
                    </td>
                  )}
                  <td className="p-4 text-left whitespace-nowrap">
                    {(() => {
                      const { date, time } = formatDateTimeParts(u.createdAt);
                      return (
                        <div>
                          <div className="text-xs font-medium text-gray-800">{date}</div>
                          {time && <div className="text-[11px] font-normal text-gray-400 mt-0.5">{time}</div>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-4 pr-6 text-right whitespace-nowrap">
                    <div className="relative inline-block text-left">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveActionMenuId(activeActionMenuId === u.id ? null : u.id);
                        }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                        title="Actions"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>

                      {activeActionMenuId === u.id && (
                        <div
                          className="origin-top-right absolute right-0 mt-1 w-44 rounded-xl shadow-lg bg-white border border-gray-100 ring-1 ring-black/5 z-30 divide-y divide-gray-100 animate-in fade-in zoom-in-95 duration-100 text-left"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="py-1">
                            <button
                              type="button"
                              onClick={() => {
                                setActiveActionMenuId(null);
                                setEditUserRoles({ email: u.emailId, name: u.name, roles: u.roles });
                              }}
                              className="w-full text-left px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer transition-colors"
                            >
                              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                              Edit Roles
                            </button>

                            {userCategory === 'admins' && !u.roles.some(r => r === 'ROLE_SUPER_ADMIN' || r === 'ROLE_ADMIN') && (
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveActionMenuId(null);
                                  setEditUserCities({ email: u.emailId, name: u.name, assigned_cities: u.assigned_cities || [] });
                                }}
                                className="w-full text-left px-3.5 py-2 text-xs font-medium text-teal-700 hover:bg-teal-50 flex items-center gap-2 cursor-pointer transition-colors"
                              >
                                <svg className="w-3.5 h-3.5 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Edit Cities
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => {
                                setActiveActionMenuId(null);
                                setChangePasswordUser({ email: u.emailId, name: u.name });
                              }}
                              className="w-full text-left px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer transition-colors"
                            >
                              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                              Change Password
                            </button>
                          </div>

                          <div className="py-1">
                            <button
                              type="button"
                              onClick={() => {
                                setActiveActionMenuId(null);
                                setDeleteUserConfirm({ id: u.id, email: u.emailId, name: u.name });
                              }}
                              className="w-full text-left px-3.5 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 flex items-center gap-2 cursor-pointer transition-colors"
                            >
                              <svg className="w-3.5 h-3.5 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete Account
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-505 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {activeUserList.length === 0 ? (
            'No records available'
          ) : (
            `Showing ${safeUserPage * userPageSize + 1} to ${Math.min((safeUserPage + 1) * userPageSize, activeUserList.length)} of ${activeUserList.length} entries`
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => fetchUsers()}
              disabled={loading}
              title="Reload current page"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCw size={12} className={loading ? 'animate-spin' : ''} />
              Reload
            </button>
            <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={userPageSize}
                onChange={(e) => {
                  setUserPageSize(Number(e.target.value));
                  setUserPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {userTotalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setUserPage(p => Math.max(0, p - 1))}
                disabled={safeUserPage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Previous
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(safeUserPage, userTotalPages).map((pageNum, idx) => {
                  if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                    return (
                      <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                        ...
                      </span>
                    );
                  }
                  const isActive = safeUserPage === pageNum;
                  return (
                    <button
                      key={`page-${pageNum}`}
                      type="button"
                      onClick={() => setUserPage(pageNum)}
                      className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                        ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                {safeUserPage + 1} / {userTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setUserPage(p => Math.min(userTotalPages - 1, p + 1))}
                disabled={safeUserPage >= userTotalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Admin Modal */}
      {showCreateAdminModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
              <h3 className="text-lg font-bold text-gray-800">Add Administrative Account</h3>
              <button onClick={() => setShowCreateAdminModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateAdminSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Full Name</label>
                  <input
                    type="text"
                    required
                    placeholder="John Doe"
                    value={adminForm.name}
                    onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="johndoe@topinteriors.com"
                    value={adminForm.emailId}
                    onChange={(e) => setAdminForm({ ...adminForm, emailId: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-805"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Mobile Number (10 digits)</label>
                  <input
                    type="text"
                    required
                    pattern="[0-9]{10}"
                    placeholder="9876543210"
                    value={adminForm.mobileNo}
                    onChange={(e) => setAdminForm({ ...adminForm, mobileNo: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-805"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Password</label>
                  <input
                    type="password"
                    required
                    placeholder="Min. 8 characters"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-805"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">State</label>
                    <select
                      value={adminForm.state}
                      onChange={(e) => handleAdminStateChange(e.target.value)}
                      className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800 cursor-pointer"
                    >
                      <option value="">Select State</option>
                      {statesList.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">City</label>
                    <select
                      value={adminForm.city}
                      onChange={(e) => setAdminForm({ ...adminForm, city: e.target.value })}
                      disabled={!adminForm.state || loadingAdminCities}
                      className={`w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800 cursor-pointer ${!adminForm.state ? 'bg-gray-50 text-gray-400' : ''}`}
                    >
                      <option value="">{loadingAdminCities ? 'Loading...' : !adminForm.state ? 'Select State First' : 'Select City'}</option>
                      {adminCitiesList.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Assign Roles</label>
                  <div className="grid grid-cols-2 gap-2.5 mt-2 bg-gray-50 p-3 rounded-xl border border-gray-200 text-xs select-none">
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('SUPER_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('SUPER_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Super Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('SALES_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('SALES_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Sales Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('LISTING_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('LISTING_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Listing Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('SUPPORT_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('SUPPORT_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Support Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('MAGAZINE_SALES')}
                        onChange={() => handleRoleCheckboxChange('MAGAZINE_SALES')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Magazine Sales
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('SEO_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('SEO_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      SEO Admin
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => setShowCreateAdminModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Roles Modal */}
      {editUserRoles && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-sm w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">Modify Privileges</span>
                <h3 className="text-lg font-bold text-gray-800 mt-0.5">{editUserRoles.name}</h3>
              </div>
              <button onClick={() => setEditUserRoles(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditUserRolesSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left font-semibold text-gray-700">
                <p className="text-xs text-gray-400 font-medium select-none">Select roles below to change this user's platform permissions:</p>

                <div className="space-y-2.5 bg-gray-50 p-4 rounded-xl border border-gray-200 text-sm select-none">
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_USER')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_USER')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Standard User (ROLE_USER)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_VENDOR')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_VENDOR')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Vendor (ROLE_VENDOR)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_SUPER_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_SUPER_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Super Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_SALES_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_SALES_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Sales Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_LISTING_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_LISTING_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Listing Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_SUPPORT_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_SUPPORT_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Support Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_MAGAZINE_SALES')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_MAGAZINE_SALES')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Magazine Sales (ROLE_MAGAZINE_SALES)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_SEO_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_SEO_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    SEO Admin (ROLE_SEO_ADMIN)
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => setEditUserRoles(null)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {changePasswordUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-sm w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">Security Administration</span>
                <h3 className="text-lg font-bold text-gray-800 mt-0.5">Change Password</h3>
              </div>
              <button
                onClick={() => {
                  setChangePasswordUser(null);
                  setNewPassword('');
                  setConfirmNewPassword('');
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleChangePasswordSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left">
                <div className="bg-teal-50/50 border border-teal-100/50 rounded-xl p-3.5 text-xs text-teal-800 font-medium">
                  <p>Changing password for:</p>
                  <p className="font-bold text-sm text-teal-950 mt-1">{changePasswordUser.name}</p>
                  <p className="text-[11px] text-teal-700 font-semibold">{changePasswordUser.email}</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">New Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Min. 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none focus:ring-1 focus:ring-[#2ab391] font-semibold text-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Re-enter new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none focus:ring-1 focus:ring-[#2ab391] font-semibold text-gray-800"
                  />
                </div>

                <p className="text-[10px] text-gray-400 font-medium leading-relaxed select-none">
                  Upon submission, the user's password will be updated instantly, and a password update email containing the new credentials will be sent to the user's registered email address.
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => {
                    setChangePasswordUser(null);
                    setNewPassword('');
                    setConfirmNewPassword('');
                  }}
                  disabled={passwordLoading}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] disabled:bg-teal-300 text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  {passwordLoading ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteUserConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[90vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-rose-50/50 flex-shrink-0 select-none">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-rose-500">Critical Action</span>
                  <h3 className="text-base font-extrabold text-rose-955 mt-0.5">Delete User Account</h3>
                </div>
              </div>
              <button
                onClick={() => {
                  setDeleteUserConfirm(null);
                  setDeleteInputConfirm('');
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleDeleteUserSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left">
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-xs text-rose-900 font-medium">
                  <p className="font-semibold text-rose-955 text-sm">Are you sure you want to delete this user?</p>
                  <p className="font-bold text-base text-rose-955 mt-2">{deleteUserConfirm.name}</p>
                  <p className="text-[11px] text-rose-700 font-semibold">{deleteUserConfirm.email}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">This will permanently delete:</p>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-gray-555 font-semibold leading-relaxed">
                    <li>The user account and platform access.</li>
                    <li>All listings, company profile logos, banners, proofs, and media files from the disk.</li>
                    <li>Review submissions, replies, review report flags, and customer inquiries.</li>
                    <li>Payment transaction history, active gateway/manual memberships.</li>
                    <li>Magazine subscription logs, print copy orders, and support tickets.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-100 space-y-3.5 text-left">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1.5">
                      To verify, type <span className="text-rose-600 font-extrabold">DELETE</span> below:
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Type DELETE"
                      value={deleteInputConfirm}
                      onChange={(e) => setDeleteInputConfirm(e.target.value)}
                      className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500 font-semibold text-gray-800"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => {
                    setDeleteUserConfirm(null);
                    setDeleteInputConfirm('');
                  }}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deleteLoading || deleteInputConfirm !== 'DELETE'}
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-rose-600/10 cursor-pointer"
                >
                  {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit City Access Modal */}
      {editUserCities && (
        <EditCitiesModal
          user={editUserCities}
          onClose={() => setEditUserCities(null)}
          onSaveSuccess={fetchUsers}
          toast={toast}
        />
      )}

      {/* View Cities Overview Modal */}
      {viewCitiesModalUser && (
        <ViewCitiesModal
          user={viewCitiesModalUser}
          onClose={() => setViewCitiesModalUser(null)}
        />
      )}

      {/* Unassigned City Audit Modal */}
      {showAuditModal && (
        <UnassignedCityAuditModal
          onClose={() => setShowAuditModal(false)}
        />
      )}
    </div>
  );
};

export default ManageUsersTab;
